'use client'

import { useEffect } from 'react'
import Image from 'next/image'
import { SignUpModal } from '@/components/auth/SignUpModal'
import { startDevLogin } from '@/lib/devLogin'
import { FilmBackdrop } from '@/app/[locale]/(marketing)/components/FilmBackdrop'
import { VideoCreditBadge } from '@/components/marketing/VideoCreditBadge'
import { BACKGROUND_VIDEOS } from '@/lib/marketing-config'

let pendingDevLogin: Promise<boolean> | null = null

function startLoginPageDevLogin(returnPath: string) {
  pendingDevLogin ??= startDevLogin(returnPath).finally(() => {
    pendingDevLogin = null
  })
  return pendingDevLogin
}

/**
 * Full-screen sign-in: the mark, one heading, Google + email, nothing else.
 * The martini.film link lives inside the modal (login variant) because the
 * dialog overlay owns pointer events. Shared by /login (`variant="login"`) and
 * the enterprise SSO gate (`variant="enterprise"`) so both get the same screen.
 */
export function LoginPage({
  returnPath,
  variant = 'login',
  onSuccess,
}: {
  returnPath: string
  variant?: 'login' | 'enterprise'
  onSuccess?: (destination: string) => void
}) {
  // Full-bleed screen: no reserved scrollbar gutter (it shows as a dark strip over the film).
  useEffect(() => {
    document.documentElement.classList.add('full-bleed-page')
    return () => document.documentElement.classList.remove('full-bleed-page')
  }, [])

  useEffect(() => {
    if (process.env.NODE_ENV === 'production' || process.env.NEXT_PUBLIC_DEV_AUTO_LOGIN !== '1') return
    // With DEV_AUTO_LOGIN=1 this leaves for the local Test User magic link.
    // Disabled or failed shortcuts are a no-op, leaving the normal sign-in.
    void startLoginPageDevLogin(returnPath)
  }, [returnPath])

  return (
    <div
      data-testid={variant === 'enterprise' ? 'enterprise-login-page' : 'login-page'}
      className="relative flex min-h-svh w-full flex-col items-center justify-center bg-background px-4 text-foreground"
    >
      {variant === 'login' && (
        <div className="absolute inset-0 overflow-hidden" aria-hidden>
          <FilmBackdrop dim={0.55} blur={4} />
          {/* Darken the middle so the form sits on the quietest part of any frame. */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_75%_at_center,rgba(0,0,0,0.3),transparent)]" />
        </div>
      )}
      {variant === 'login' && (
        <div className="absolute bottom-6 end-6 z-10">
          <VideoCreditBadge video={BACKGROUND_VIDEOS[0]} />
        </div>
      )}
      {variant === 'enterprise' && (
        <header className="absolute start-6 top-6 flex items-center gap-3 md:start-8 md:top-8">
          {/* eslint-disable-next-line i18next/no-literal-string -- brand */}
          <Image src="/whitemartini.svg" alt="Martini" width={28} height={28} priority />
          <div className="flex items-center gap-2 text-sm font-medium tracking-tight">
            {/* eslint-disable-next-line i18next/no-literal-string -- brand */}
            <span>Martini</span>
            <span className="h-4 w-px bg-border" aria-hidden="true" />
            <span className="text-muted-foreground">Enterprise</span>
          </div>
        </header>
      )}

      <SignUpModal
        isOpen
        onClose={() => {}}
        defaultMode="signin"
        returnPath={returnPath}
        variant={variant}
        onSuccess={onSuccess}
      />
    </div>
  )
}
